import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, api, del, post, toastError, docViewUrl, API_URL, getToken } from "@/lib/api";
import { DOCUMENT_CATEGORIES, label } from "@/lib/labels";
import { fmtDateTime, fileSize } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LoadingState, EmptyState } from "./States";
import { Upload, Link2, FileText, Image as ImageIcon, Download, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const DocumentsPanel = ({ entityType, entityId, compact = false, defaultCategory = "annet" }) => {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("upload");
  const [form, setForm] = useState({ category: defaultCategory, title: "", description: "", revision: "", external_url: "" });
  const [file, setFile] = useState(null);
  const fileRef = useRef();
  const key = ["documents", entityType, entityId];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => get("/documents", { entity_type: entityType, entity_id: entityId }), enabled: !!entityId });

  const upload = useMutation({
    mutationFn: async () => {
      if (mode === "link") {
        return post("/documents/link", { entity_type: entityType, entity_id: entityId, category: form.category, title: form.title, external_url: form.external_url, description: form.description || null, revision: form.revision || null });
      }
      const fd = new FormData();
      fd.append("entity_type", entityType);
      fd.append("entity_id", entityId);
      fd.append("category", form.category);
      if (form.title) fd.append("title", form.title);
      if (form.description) fd.append("description", form.description);
      if (form.revision) fd.append("revision", form.revision);
      fd.append("file", file);
      return api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
    },
    onSuccess: () => {
      toast.success(mode === "link" ? "Lenke lagt til" : "Dokument lastet opp");
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ["documents"] });
      setOpen(false);
      setFile(null);
      setForm({ category: defaultCategory, title: "", description: "", revision: "", external_url: "" });
    },
    onError: (e) => toastError(e),
  });
  const remove = useMutation({
    mutationFn: (id) => del(`/documents/${id}`),
    onSuccess: () => {
      toast.success("Dokument slettet");
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toastError(e),
  });

  const download = async (d) => {
    try {
      const r = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_name || d.title;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toastError(e);
    }
  };

  const docs = data || [];
  const images = docs.filter((d) => d.is_image && d.path);

  return (
    <div data-testid="documents-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-muted-foreground">{docs.length} dokument{docs.length === 1 ? "" : "er"}</div>
        {can("documents:write") && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { setMode("link"); setOpen(true); }} data-testid="documents-add-link-button">
              <Link2 className="h-4 w-4 mr-1" /> Lenke
            </Button>
            <Button size="sm" onClick={() => { setMode("upload"); setOpen(true); }} data-testid="documents-upload-button">
              <Upload className="h-4 w-4 mr-1" /> Last opp
            </Button>
          </div>
        )}
      </div>
      {isLoading ? (
        <LoadingState className="py-6" />
      ) : docs.length === 0 ? (
        <EmptyState title="Ingen dokumenter" description="Last opp tegninger, FDV, kontrakter eller bilder, eller legg til en ekstern lenke (BIM/Revit)." icon={FileText} className="py-8" testId="documents-empty" />
      ) : (
        <>
          {images.length > 0 && !compact && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-3">
              {images.map((d) => (
                <a key={d.id} href={docViewUrl(d.id)} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden rounded-md border bg-muted" data-testid={`document-image-${d.id}`}>
                  <img src={docViewUrl(d.id)} alt={d.title} className="h-full w-full object-cover" loading="lazy" />
                </a>
              ))}
            </div>
          )}
          <ul className="divide-y rounded-md border">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`document-row-${d.id}`}>
                {d.is_image ? <ImageIcon className="h-4 w-4 text-muted-foreground shrink-0" /> : d.external_url ? <Link2 className="h-4 w-4 text-muted-foreground shrink-0" /> : <FileText className="h-4 w-4 text-muted-foreground shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{d.title}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {label(d.category)} {d.revision ? `· rev ${d.revision}` : ""} · {d.uploaded_by_name || "–"} · {fmtDateTime(d.uploaded_at)} {d.size ? `· ${fileSize(d.size)}` : ""}
                  </div>
                </div>
                {d.external_url ? (
                  <a href={d.external_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" aria-label="Åpne lenke">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                ) : (
                  <button onClick={() => download(d)} className="text-muted-foreground hover:text-foreground" aria-label="Last ned" data-testid={`document-download-${d.id}`}>
                    <Download className="h-4 w-4" />
                  </button>
                )}
                {can("documents:write") && (
                  <button onClick={() => remove.mutate(d.id)} className="text-muted-foreground hover:text-destructive" aria-label="Slett" data-testid={`document-delete-${d.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-[480px] max-w-[92vw] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{mode === "link" ? "Legg til ekstern lenke" : "Last opp dokument"}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <div>
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="document-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DOCUMENT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{label(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {mode === "link" ? (
              <>
                <div><Label>Tittel *</Label><Input data-testid="document-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="F.eks. Revit-modell rev C" /></div>
                <div><Label>URL *</Label><Input data-testid="document-url-input" value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://…" /></div>
              </>
            ) : (
              <>
                <div>
                  <Label>Fil *</Label>
                  <div
                    className="mt-1 rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground cursor-pointer hover:bg-muted/50"
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]); }}
                    data-testid="document-dropzone"
                  >
                    {file ? <span className="text-foreground font-medium">{file.name} ({fileSize(file.size)})</span> : "Klikk eller dra fil hit (PDF, bilder, DWG, IFC, RVT, Office – maks 25 MB)"}
                  </div>
                  <input ref={fileRef} type="file" className="hidden" data-testid="document-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </div>
                <div><Label>Tittel</Label><Input data-testid="document-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Standard: filnavn" /></div>
              </>
            )}
            <div><Label>Revisjon</Label><Input value={form.revision} onChange={(e) => setForm({ ...form, revision: e.target.value })} placeholder="F.eks. C" /></div>
            <div><Label>Beskrivelse</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <Button className="w-full" disabled={upload.isPending || (mode === "upload" ? !file : !form.title || !form.external_url)} onClick={() => upload.mutate()} data-testid="document-submit-button">
              {upload.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {mode === "link" ? "Legg til lenke" : "Last opp"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
